/**
 * Single-click PDF export via DOM capture.
 *
 * Captures the live page with html2canvas-pro (modern-CSS fork —
 * handles MUI v6's color-mix / oklch) and writes the result into a
 * multi-page A4 PDF with jsPDF. No browser print dialog.
 *
 * Page breaks: each top-level "section" (print-header, top-level
 * Accordion/Paper, or anything marked `data-pdf-page-break-before`) is
 * treated as atomic. We measure each section's rectangle before
 * capture, then pack consecutive sections onto pages greedily — a page
 * ends the moment the next section wouldn't fit whole. Inter-section
 * whitespace is dropped, so each page ends cleanly at a section
 * boundary. A section taller than a page is hard-cut as a last
 * resort.
 *
 * Print-mode layout (expanded accordions, hidden toolbar, print-only
 * header) is triggered by adding a `capturing` class to body; the rules
 * live in src/styles/print.css alongside the @media print ones so we
 * don't duplicate layout logic.
 */

import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';

export interface PdfExportArgs {
  filename: string;
}

// A4 portrait, mm.
const PAGE_W_MM = 210;
const PAGE_H_MM = 297;
const MARGIN_X_MM = 10;
const MARGIN_Y_MM = 10;
const CONTENT_W_MM = PAGE_W_MM - 2 * MARGIN_X_MM;
const CONTENT_H_MM = PAGE_H_MM - 2 * MARGIN_Y_MM;

const CAPTURE_SCALE = 2;   // html2canvas scale — 2x for sharp zoom on ≈ 2400×tall canvas.
const JPEG_QUALITY = 0.92; // Balance between file size and text crispness when zoomed.

interface Section {
  /** document-relative top in CSS pixels */
  top: number;
  /** document-relative bottom in CSS pixels */
  bottom: number;
}

/**
 * Collect top-level section rectangles (document-relative, CSS
 * pixels). A "section" is the print-header, a top-level MUI
 * Accordion/Paper, or any element explicitly marked
 * `data-pdf-page-break-before`. Nested Accordions/Papers are skipped
 * (their ancestor already represents them) — unless the nested element
 * carries the forced attribute, in which case it's promoted to a
 * top-level section anyway so it can start its own page.
 */
function collectSections(): Section[] {
  const selector =
    '.print-header, .MuiAccordion-root, .MuiPaper-root, [data-pdf-page-break-before]';
  const nodes = document.querySelectorAll<HTMLElement>(selector);
  const raw: Section[] = [];
  nodes.forEach((el) => {
    // Skip hidden elements (e.g., AppBar / dialogs under capture rules).
    if (el.offsetParent === null && !el.classList.contains('print-header')) {
      return;
    }
    const isForced = el.hasAttribute('data-pdf-page-break-before');
    if (!isForced) {
      // Skip nested sections — an ancestor section already covers us.
      let p = el.parentElement;
      while (p && p !== document.body) {
        if (
          p.classList.contains('MuiAccordion-root') ||
          p.classList.contains('MuiPaper-root') ||
          p.hasAttribute('data-pdf-page-break-before')
        ) {
          return;
        }
        p = p.parentElement;
      }
    }
    const rect = el.getBoundingClientRect();
    raw.push({
      top: rect.top + window.scrollY,
      bottom: rect.bottom + window.scrollY,
    });
  });

  // Sort by top; merge any overlaps so a forced-promoted nested section
  // doesn't double-count with its ancestor.
  raw.sort((a, b) => a.top - b.top);
  const merged: Section[] = [];
  for (const s of raw) {
    const last = merged[merged.length - 1];
    if (last && s.top < last.bottom - 0.5) {
      last.bottom = Math.max(last.bottom, s.bottom);
    } else {
      merged.push({ ...s });
    }
  }
  return merged;
}

/**
 * Render a horizontal slice of the capture canvas into its own canvas
 * so we can feed it to jsPDF.addImage without clipping hacks. JPEG at
 * 0.92 quality — visually indistinguishable from PNG for a rendered
 * UI, but much smaller in the final PDF. Pages drawn on a white
 * background so JPEG's lack of transparency is a non-issue.
 */
function sliceCanvas(
  source: HTMLCanvasElement,
  startPx: number,
  heightPx: number,
): string {
  const page = document.createElement('canvas');
  page.width = source.width;
  page.height = Math.max(1, heightPx);
  const ctx = page.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, page.width, page.height);
    ctx.drawImage(source, 0, -startPx);
  }
  return page.toDataURL('image/jpeg', JPEG_QUALITY);
}

export async function exportPagePDF(args: PdfExportArgs): Promise<void> {
  document.body.classList.add('capturing');
  // Let the browser lay out the capturing-mode styles before we query
  // element positions and hand the tree to html2canvas.
  await new Promise<void>((r) => requestAnimationFrame(() => r()));

  try {
    // 1. Record section rectangles in DOM pixels (document-relative).
    const sections = collectSections();

    // 2. Capture the whole page.
    const canvas = await html2canvas(document.body, {
      backgroundColor: '#ffffff',
      scale: CAPTURE_SCALE,
      useCORS: true,
      logging: false,
      onclone: (doc) => {
        doc.body.classList.add('capturing');
      },
    });

    // 3. Convert DOM px to mm using the captured canvas width.
    //    DOM px -> canvas px:  multiply by CAPTURE_SCALE
    //    canvas px -> mm:      CONTENT_W_MM / canvas.width
    const mmPerCanvasPx = CONTENT_W_MM / canvas.width;
    const domPxToMm = (px: number) => px * CAPTURE_SCALE * mmPerCanvasPx;
    const secsMm = sections
      .map((s) => ({ top: domPxToMm(s.top), bottom: domPxToMm(s.bottom) }))
      .filter((s) => s.bottom > s.top + 0.5);

    // 4. Build PDF, one page at a time. Each page contains as many
    //    whole consecutive sections as will fit in CONTENT_H_MM.
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    let firstPage = true;

    const emitPage = (startMm: number, endMm: number) => {
      const startPx = Math.round(startMm / mmPerCanvasPx);
      const endPx = Math.round(endMm / mmPerCanvasPx);
      const sliceHeightPx = Math.max(1, endPx - startPx);
      const sliceDataUrl = sliceCanvas(canvas, startPx, sliceHeightPx);
      if (!firstPage) pdf.addPage();
      firstPage = false;
      pdf.addImage(
        sliceDataUrl,
        'JPEG',
        MARGIN_X_MM,
        MARGIN_Y_MM,
        CONTENT_W_MM,
        Math.min(endMm - startMm, CONTENT_H_MM),
      );
    };

    if (secsMm.length === 0) {
      // Fall back to a single-page capture if we couldn't identify any
      // section — better than producing an empty PDF.
      const totalMm = canvas.height * mmPerCanvasPx;
      emitPage(0, Math.min(totalMm, CONTENT_H_MM));
    } else {
      let idx = 0;
      while (idx < secsMm.length) {
        const pageStart = secsMm[idx].top;
        const sectionHeight = secsMm[idx].bottom - pageStart;

        // A section taller than a page can't be atomic — hard-cut it
        // across as many pages as it needs.
        if (sectionHeight > CONTENT_H_MM + 0.5) {
          let s = pageStart;
          const end = secsMm[idx].bottom;
          while (s < end - 0.5) {
            const e = Math.min(s + CONTENT_H_MM, end);
            emitPage(s, e);
            s = e;
          }
          idx += 1;
          continue;
        }

        // Pack whole sections onto this page while they fit.
        let pageEnd = secsMm[idx].bottom;
        let next = idx + 1;
        while (
          next < secsMm.length
          && secsMm[next].bottom - pageStart <= CONTENT_H_MM + 0.5
        ) {
          pageEnd = secsMm[next].bottom;
          next += 1;
        }
        emitPage(pageStart, pageEnd);
        idx = next;
      }
    }

    pdf.save(args.filename);
  } finally {
    document.body.classList.remove('capturing');
  }
}
